import React from 'react'
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import useBatchRegenerate, { describeFailedTables } from '../hooks/useBatchRegenerate'
import { extractErrorMessage } from '../utils/apitools'
import RegenerateWizard from '../components/batch/regenerate/RegenerateWizard'
import PageHeader from '../components/system/PageHeader'

/** Regenerate Tables (#1809): the page loads the folder, then the walkthrough
 *  asks one question per step and the page submits. */
const BatchRegenerate: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const snackbar = useSnackbar()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const sourceFolder = router.params.sourceFolder || ''
  const sourceFolderName = router.params.sourceFolderName || sourceFolder.split('/').pop() || 'Unknown'

  const batch = useBatchRegenerate({
    customer, job, sourceFolder,
    userEmail: account.user?.session_info?.email || undefined,
  })

  const displayDestination = `My Tables/${batch.destinationFolder}`
  const sourceEmpty = !batch.loadingSource && !batch.sourceError && batch.sourceTables.length === 0

  const handleConfirmGenerate = async () => {
    let result
    try {
      result = await batch.submit()
    } catch (err) {
      snackbar.error(`Regeneration failed: ${extractErrorMessage(err)}`)
      return
    }
    const { count, failed, error } = result
    const refused = failed.length > 0 ? ` ${failed.length} not saved — ${describeFailedTables(failed)}` : ''
    if (count === 0) {
      // Nothing saved: stay on Review so the user can change the settings.
      snackbar.error(`Regeneration failed: ${error || 'no tables were generated'}.${refused}`)
      return
    }
    const verb = batch.overwrite ? 'Overwrote' : 'Regenerated'
    const done = `${verb} ${count} table${count !== 1 ? 's' : ''} in ${displayDestination}`
    if (error) {
      snackbar.error(`${done}, then stopped: ${error}.${refused}`)
    } else if (failed.length > 0) {
      snackbar.error(`${done}.${refused}`)
    } else {
      snackbar.info(done)
    }
    account.openPage('tables')
    account.reloadTOC()
  }

  const handleCancel = () => {
    account.openPage('tables')
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title={`Regenerate Tables from "${sourceFolderName}"`}
        subtitle={batch.loadingSource ? undefined : `${batch.sourceTables.length.toLocaleString()} source table${batch.sourceTables.length !== 1 ? 's' : ''}`}
        actions={
          <Button variant="outlined" color="inherit" onClick={handleCancel} startIcon={<CloseIcon />}>
            Cancel
          </Button>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
        {batch.loadingSource ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 6 }}>
            <CircularProgress color="info" />
            <Typography variant="body1">Loading tables. Usually takes less than a minute.</Typography>
            {batch.totalCount > 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="regenerate-load-count">
                {batch.loadedCount.toLocaleString()} of {batch.totalCount.toLocaleString()}
              </Typography>
            )}
          </Box>
        ) : batch.sourceError ? (
          <Alert severity="error" sx={{ m: 2 }}>{batch.sourceError}</Alert>
        ) : sourceEmpty ? (
          <Alert severity="info" sx={{ m: 2 }}>This folder has no tables.</Alert>
        ) : (
          <>
            {batch.progress && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 6 }}>
                <CircularProgress color="info" />
                <Typography variant="body1" data-testid="regenerate-progress">
                  Generating tables: {batch.progress.done.toLocaleString()} of {batch.progress.total.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep this page open until it finishes.
                </Typography>
              </Box>
            )}
            {/* Hidden, not unmounted, while generating: if nothing is saved
                the user is back on Review with every choice kept. */}
            <Box sx={{ flex: 1, minHeight: 0, display: batch.progress ? 'none' : 'flex', flexDirection: 'column' }}>
              <RegenerateWizard
                batch={batch}
                customer={customer}
                job={job}
                onGenerate={handleConfirmGenerate}
                onCancel={handleCancel}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default BatchRegenerate
